/**
 * SeasonHistoryService.ts
 * Archives season data when a season completes
 * - Team standings with playoff results
 * - Player career totals
 * - League champions
 * - End-of-season awards
 */

import { supabase, isSupabaseConfigured } from './supabase';
import { processRetirements } from './retirement';

// ============================================================
// TYPES
// ============================================================

export interface TeamSeasonRecord {
  id: string;
  season_year: number;
  league_id: string;
  team_id: string;
  team_name: string;
  team_city: string;
  wins: number;
  losses: number;
  runs_scored: number;
  runs_allowed: number;
  division: string;
  conference: string;
  final_rank: number;
  playoff_result: string;
  championship: boolean;
  run_diff: number;
  win_pct: number;
  games_back: number;
}

// ============================================================
// ARCHIVE A COMPLETED SEASON
// ============================================================

export async function archiveSeason(leagueId: string, seasonYear: number): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;

  console.log(`📦 Archiving season ${seasonYear} for league ${leagueId}...`);

  try {
    await archiveTeamStandings(leagueId, seasonYear);
  } catch (e: any) {
    console.error('❌ archiveTeamStandings failed:', e.message);
  }

  try {
    await archivePlayerCareerStats(leagueId, seasonYear);
  } catch (e: any) {
    console.error('❌ archivePlayerCareerStats failed:', e.message);
  }

  try {
    await determineChampion(leagueId, seasonYear);
  } catch (e: any) {
    console.error('❌ determineChampion failed:', e.message);
  }

  try {
    await generateSeasonAwards(leagueId, seasonYear);
  } catch (e: any) {
    console.error('❌ generateSeasonAwards failed:', e.message);
  }

  try {
    const retired = await processRetirements(leagueId, seasonYear);
    if (retired > 0) console.log(`👴 ${retired} players retired this season`);
  } catch (e: any) {
    console.error('❌ processRetirements failed:', e.message);
  }

  console.log(`✅ Season ${seasonYear} archived!`);
}

// ============================================================
// TEAM STANDINGS ARCHIVE
// ============================================================

async function archiveTeamStandings(leagueId: string, seasonYear: number): Promise<void> {
  if (!supabase) return;

  // Get current standings
  const { data: standings } = await supabase
    .from('league_standings')
    .select('*')
    .eq('league_id', leagueId)
    .order('wins', { ascending: false });

  if (!standings || standings.length === 0) {
    console.warn('⚠️ No standings to archive');
    return;
  }

  // Get team info for names
  const teamIds = standings.map((s: any) => s.team_id);
  const { data: teams } = await supabase
    .from('teams')
    .select('id, name, city')
    .in('id', teamIds);

  const teamMap = new Map<string, any>();
  (teams || []).forEach((t: any) => teamMap.set(t.id, t));

  // Find division leaders for rank calculation
  const divGroups = new Map<string, any[]>();
  standings.forEach((s: any) => {
    const divKey = `${s.conference}-${s.division}`;
    if (!divGroups.has(divKey)) divGroups.set(divKey, []);
    divGroups.get(divKey)!.push(s);
  });

  // Sort each division by wins
  divGroups.forEach(divStandings => {
    divStandings.sort((a: any, b: any) => {
      const pctA = (a.wins + a.losses) > 0 ? a.wins / (a.wins + a.losses) : 0;
      const pctB = (b.wins + b.losses) > 0 ? b.wins / (b.wins + b.losses) : 0;
      return pctB - pctA;
    });
  });

  // Create archive records
  const records: any[] = [];

  for (const standing of standings) {
    const team = teamMap.get(standing.team_id);
    const divKey = `${standing.conference}-${standing.division}`;
    const divStandings = divGroups.get(divKey) || [];
    const rank = divStandings.findIndex((s: any) => s.team_id === standing.team_id) + 1;

    const wins = standing.wins || 0;
    const losses = standing.losses || 0;
    const total = wins + losses;
    const winPct = total > 0 ? wins / total : 0;
    const rs = standing.runs_scored || 0;
    const ra = standing.runs_allowed || 0;

    // Games back from division leader
    const leader = divStandings[0];
    let gamesBack = 0;
    if (leader && leader.team_id !== standing.team_id) {
      gamesBack = ((leader.wins - wins) + (losses - leader.losses)) / 2;
    }

    records.push({
      id: `hist_${seasonYear}_${standing.team_id}`,
      season_year: seasonYear,
      league_id: leagueId,
      team_id: standing.team_id,
      team_name: team?.name || 'Unknown',
      team_city: team?.city || '',
      wins,
      losses,
      runs_scored: rs,
      runs_allowed: ra,
      division: standing.division,
      conference: standing.conference,
      final_rank: rank,
      playoff_result: 'pending',  // Will be updated after playoffs
      championship: false,
      run_diff: rs - ra,
      win_pct: parseFloat(winPct.toFixed(3)),
      games_back: parseFloat(gamesBack.toFixed(1)),
      archived_at: new Date().toISOString(),
    });
  }

  // Upsert all records
  if (records.length > 0) {
    const { error } = await supabase.from('season_history').upsert(records);
    if (error) {
      console.error('❌ Failed to archive standings:', error);
    } else {
      console.log(`📦 Archived ${records.length} team season records`);
    }
  }
}

// ============================================================
// PLAYER CAREER STATS
// ============================================================

async function archivePlayerCareerStats(leagueId: string, seasonYear: number): Promise<void> {
  if (!supabase) return;

  // Get all player season stats for this league
  const { data: seasonStats, error: queryErr } = await supabase
    .from('player_season_stats')
    .select('*')
    .eq('league_id', leagueId);

  if (queryErr) {
    console.error('❌ Failed to query player_season_stats:', queryErr.message);
    return;
  }

  if (!seasonStats || seasonStats.length === 0) {
    console.log('📦 No player stats to archive');
    return;
  }

  console.log(`📦 Archiving ${seasonStats.length} player stats...`);

  // Get existing career records in bulk
  const playerIds = seasonStats.map(s => s.player_id);
  const { data: existingCareers } = await supabase
    .from('player_career_stats')
    .select('*')
    .in('player_id', playerIds);

  const careerMap = new Map<string, any>();
  (existingCareers || []).forEach(c => careerMap.set(c.player_id, c));

  const toInsert: any[] = [];
  const toUpdate: any[] = [];

  for (const stat of seasonStats) {
    const existing = careerMap.get(stat.player_id);
    const atBats = stat.at_bats || 0;
    const hits = stat.hits || 0;
    const ip = stat.innings_pitched || 0;
    const er = stat.earned_runs || 0;

    if (existing) {
      toUpdate.push({
        player_id: stat.player_id,
        total_games: (existing.total_games || 0) + (stat.games || 0),
        total_plate_appearances: (existing.total_plate_appearances || 0) + (stat.plate_appearances || 0),
        total_at_bats: (existing.total_at_bats || 0) + atBats,
        total_runs: (existing.total_runs || 0) + (stat.runs || 0),
        total_hits: (existing.total_hits || 0) + hits,
        total_doubles: (existing.total_doubles || 0) + (stat.doubles || 0),
        total_triples: (existing.total_triples || 0) + (stat.triples || 0),
        total_home_runs: (existing.total_home_runs || 0) + (stat.home_runs || 0),
        total_rbi: (existing.total_rbi || 0) + (stat.rbi || 0),
        total_walks: (existing.total_walks || 0) + (stat.walks || 0),
        total_strikeouts: (existing.total_strikeouts || 0) + (stat.strikeouts || 0),
        total_stolen_bases: (existing.total_stolen_bases || 0) + (stat.stolen_bases || 0),
        career_avg: ((existing.total_hits || 0) + hits) > 0 && ((existing.total_at_bats || 0) + atBats) > 0
          ? parseFloat((((existing.total_hits || 0) + hits) / ((existing.total_at_bats || 0) + atBats)).toFixed(3)) : 0,
        total_wins: (existing.total_wins || 0) + (stat.wins || 0),
        total_losses: (existing.total_losses || 0) + (stat.losses || 0),
        total_innings_pitched: parseFloat(((existing.total_innings_pitched || 0) + ip).toFixed(1)),
        total_earned_runs: (existing.total_earned_runs || 0) + er,
        total_pitching_strikeouts: (existing.total_pitching_strikeouts || 0) + (stat.strikeouts_pitched || 0),
        total_hits_allowed: (existing.total_hits_allowed || 0) + (stat.hits_allowed || 0),
        total_walks_allowed: (existing.total_walks_allowed || 0) + (stat.walks_pitching || 0),
        total_home_runs_allowed: (existing.total_home_runs_allowed || 0) + (stat.home_runs_allowed || 0),
        total_pitches_thrown: (existing.total_pitches_thrown || 0) + (stat.pitches_thrown || 0),
        career_era: ((existing.total_innings_pitched || 0) + ip) > 0
          ? parseFloat((((existing.total_earned_runs || 0) + er) * 9 / ((existing.total_innings_pitched || 0) + ip)).toFixed(2)) : 0,
        seasons_played: (existing.seasons_played || 0) + 1,
        last_season: seasonYear,
        updated_at: new Date().toISOString(),
      });
    } else {
      toInsert.push({
        player_id: stat.player_id,
        total_games: stat.games || 0,
        total_plate_appearances: stat.plate_appearances || 0,
        total_at_bats: atBats,
        total_runs: stat.runs || 0,
        total_hits: hits,
        total_doubles: stat.doubles || 0,
        total_triples: stat.triples || 0,
        total_home_runs: stat.home_runs || 0,
        total_rbi: stat.rbi || 0,
        total_walks: stat.walks || 0,
        total_strikeouts: stat.strikeouts || 0,
        total_stolen_bases: stat.stolen_bases || 0,
        career_avg: atBats > 0 ? parseFloat((hits / atBats).toFixed(3)) : 0,
        total_wins: stat.wins || 0,
        total_losses: stat.losses || 0,
        total_innings_pitched: ip,
        total_earned_runs: er,
        total_pitching_strikeouts: stat.strikeouts_pitched || 0,
        total_hits_allowed: stat.hits_allowed || 0,
        total_walks_allowed: stat.walks_pitching || 0,
        total_home_runs_allowed: stat.home_runs_allowed || 0,
        total_pitches_thrown: stat.pitches_thrown || 0,
        career_era: ip > 0 ? parseFloat(((er * 9) / ip).toFixed(2)) : 0,
        seasons_played: 1,
        last_season: seasonYear,
        updated_at: new Date().toISOString(),
      });
    }
  }

  // Batch insert new career records
  if (toInsert.length > 0) {
    const batchSize = 50;
    for (let i = 0; i < toInsert.length; i += batchSize) {
      const batch = toInsert.slice(i, i + batchSize);
      const { error } = await supabase.from('player_career_stats').upsert(batch, { onConflict: 'player_id' });
      if (error) console.error(`❌ PCS insert batch ${i}:`, error.message);
    }
    console.log(`📦 Created ${toInsert.length} new career records`);
  }

  // Batch update existing career records
  if (toUpdate.length > 0) {
    const batchSize = 50;
    for (let i = 0; i < toUpdate.length; i += batchSize) {
      const batch = toUpdate.slice(i, i + batchSize);
      for (const record of batch) {
        const { error } = await supabase.from('player_career_stats')
          .update(record)
          .eq('player_id', record.player_id);
        if (error) console.error(`❌ PCS update ${record.player_id}:`, error.message);
      }
    }
    console.log(`📦 Updated ${toUpdate.length} existing career records`);
  }

  // Also save to season_history_stats for per-season snapshots
  const snapshotRecords = seasonStats.map(s => ({
    player_id: s.player_id,
    team_id: s.team_id,
    season_year: seasonYear,
    league_id: leagueId,
    games: s.games || 0,
    plate_appearances: s.plate_appearances || 0,
    at_bats: s.at_bats || 0,
    runs: s.runs || 0,
    hits: s.hits || 0,
    doubles: s.doubles || 0,
    triples: s.triples || 0,
    home_runs: s.home_runs || 0,
    rbi: s.rbi || 0,
    walks: s.walks || 0,
    strikeouts: s.strikeouts || 0,
    stolen_bases: s.stolen_bases || 0,
    avg: s.avg || 0,
    wins: s.wins || 0,
    losses: s.losses || 0,
    era: s.era || 0,
    innings_pitched: s.innings_pitched || 0,
    hits_allowed: s.hits_allowed || 0,
    earned_runs: s.earned_runs || 0,
    walks_pitching: s.walks_pitching || 0,
    strikeouts_pitched: s.strikeouts_pitched || 0,
    home_runs_allowed: s.home_runs_allowed || 0,
    pitches_thrown: s.pitches_thrown || 0,
  }));

  if (snapshotRecords.length > 0) {
    const batchSize = 50;
    for (let i = 0; i < snapshotRecords.length; i += batchSize) {
      const batch = snapshotRecords.slice(i, i + batchSize);
      const { error } = await supabase.from('season_history_stats').upsert(batch, {
        onConflict: 'player_id,season_year'
      });
      if (error) console.error(`❌ SHS batch ${i}:`, error.message);
    }
    console.log(`📦 Saved ${snapshotRecords.length} season history snapshots`);
  }
}

// ============================================================
// CHAMPION DETERMINATION
// ============================================================

async function determineChampion(leagueId: string, seasonYear: number): Promise<void> {
  if (!supabase) return;

  // Check if there's a completed playoff bracket
  const { data: bracket } = await supabase
    .from('playoff_brackets')
    .select('champion, status')
    .eq('league_id', leagueId)
    .single();

  let championId: string | null = null;
  let championName: string | null = null;

  if (bracket?.champion) {
    championId = bracket.champion;
  } else {
    // No playoffs — champion is the team with the best record
    const { data: standings } = await supabase
      .from('league_standings')
      .select('team_id, wins, losses')
      .eq('league_id', leagueId)
      .order('wins', { ascending: false })
      .limit(1);

    if (standings && standings.length > 0) {
      championId = standings[0].team_id;
    }
  }

  if (championId) {
    // Get team name
    const { data: team } = await supabase
      .from('teams')
      .select('city, name')
      .eq('id', championId)
      .single();

    championName = team ? `${team.city} ${team.name}` : 'Unknown';

    // Update league with champion
    await supabase.from('leagues').update({
      champion_team_id: championId,
      champion_name: championName,
      season_year: seasonYear,
    }).eq('id', leagueId);

    // Mark team in season_history as champion
    await supabase.from('season_history').update({
      championship: true,
      playoff_result: 'won_ws',
    }).eq('team_id', championId).eq('season_year', seasonYear);

    console.log(`🏆 ${championName} wins the championship!`);
  }
}

// ============================================================
// SEASON AWARDS
// ============================================================

async function generateSeasonAwards(leagueId: string, seasonYear: number): Promise<void> {
  if (!supabase) return;

  // Get league teams
  const { data: teams } = await supabase
    .from('teams')
    .select('id, city, name')
    .eq('league_id', leagueId);

  if (!teams || teams.length === 0) return;

  const teamIds = teams.map((t: any) => t.id);
  const teamMap = new Map<string, string>();
  teams.forEach((t: any) => teamMap.set(t.id, `${t.city} ${t.name}`));

  // Get all player season stats
  const { data: stats } = await supabase
    .from('player_season_stats')
    .select('*')
    .in('team_id', teamIds);

  if (!stats || stats.length === 0) return;

  const awards: any[] = [];

  // MVP: Best hitter by OPS (min 100 PA)
  const qualifiedHitters = stats
    .filter((s: any) => (s.plate_appearances || 0) >= 100 && (s.innings_pitched || 0) === 0)
    .sort((a: any, b: any) => {
      const opsA = ((a.hits || 0) + (a.walks || 0)) / Math.max(1, (a.at_bats || 0) + (a.walks || 0)) + ((a.hits || 0) + (a.doubles || 0) + 2 * (a.triples || 0) + 3 * (a.home_runs || 0)) / Math.max(1, a.at_bats || 1);
      const opsB = ((b.hits || 0) + (b.walks || 0)) / Math.max(1, (b.at_bats || 0) + (b.walks || 0)) + ((b.hits || 0) + (b.doubles || 0) + 2 * (b.triples || 0) + 3 * (b.home_runs || 0)) / Math.max(1, b.at_bats || 1);
      return opsB - opsA;
    });

  if (qualifiedHitters.length > 0) {
    const mvp = qualifiedHitters[0];
    const avg = (mvp.at_bats || 0) > 0 ? ((mvp.hits || 0) / mvp.at_bats).toFixed(3) : '.000';
    awards.push({
      id: `award_${seasonYear}_${leagueId}_mvp`,
      season_year: seasonYear,
      league_id: leagueId,
      award_type: 'mvp',
      player_id: mvp.player_id,
      player_name: mvp.player_name || 'Unknown',
      team_id: mvp.team_id,
      team_name: teamMap.get(mvp.team_id) || '',
      primary_stat: `${avg} AVG, ${mvp.home_runs || 0} HR, ${mvp.rbi || 0} RBI`,
    });
  }

  // Cy Young: Best pitcher by ERA (min 50 IP)
  const qualifiedPitchers = stats
    .filter((s: any) => (s.innings_pitched || 0) >= 50)
    .sort((a: any, b: any) => {
      const eraA = (a.innings_pitched || 0) > 0 ? ((a.earned_runs || 0) * 9) / a.innings_pitched : 99;
      const eraB = (b.innings_pitched || 0) > 0 ? ((b.earned_runs || 0) * 9) / b.innings_pitched : 99;
      return eraA - eraB;
    });

  if (qualifiedPitchers.length > 0) {
    const cy = qualifiedPitchers[0];
    const era = (cy.innings_pitched || 0) > 0 ? ((cy.earned_runs || 0) * 9 / cy.innings_pitched).toFixed(2) : '0.00';
    awards.push({
      id: `award_${seasonYear}_${leagueId}_cy_young`,
      season_year: seasonYear,
      league_id: leagueId,
      award_type: 'cy_young',
      player_id: cy.player_id,
      player_name: cy.player_name || 'Unknown',
      team_id: cy.team_id,
      team_name: teamMap.get(cy.team_id) || '',
      primary_stat: `${cy.wins || 0}-${cy.losses || 0}, ${era} ERA, ${cy.strikeouts_pitched || 0} K`,
    });
  }

  // Rookie of the Year: Best young player (simplified — highest OPS among new players)
  // TODO: Track debut season to properly identify rookies

  // Save awards
  if (awards.length > 0) {
    const { error } = await supabase.from('season_awards').upsert(awards);
    if (error) {
      console.error('❌ Failed to save awards:', error);
    } else {
      console.log(`🏅 Generated ${awards.length} season awards`);
    }
  }
}

// ============================================================
// GETTERS FOR DISPLAY
// ============================================================

export async function getTeamHistory(teamId: string): Promise<TeamSeasonRecord[]> {
  if (!isSupabaseConfigured() || !supabase) return [];

  const { data } = await supabase
    .from('season_history')
    .select('*')
    .eq('team_id', teamId)
    .order('season_year', { ascending: false });

  return (data || []) as TeamSeasonRecord[];
}

export async function getPlayerCareerStats(playerId: string): Promise<any | null> {
  if (!isSupabaseConfigured() || !supabase) return null;

  const { data } = await supabase
    .from('player_career_stats')
    .select('*')
    .eq('player_id', playerId)
    .single();

  return data;
}

export async function getPlayerSeasonHistory(playerId: string): Promise<any[]> {
  if (!isSupabaseConfigured() || !supabase) return [];

  const { data } = await supabase
    .from('player_season_stats')
    .select('*')
    .eq('player_id', playerId)
    .order('last_updated', { ascending: false });

  return data || [];
}

export async function getLeagueChampions(leagueId: string): Promise<any[]> {
  if (!isSupabaseConfigured() || !supabase) return [];

  const { data } = await supabase
    .from('season_history')
    .select('season_year, team_city, team_name, wins, losses, championship')
    .eq('league_id', leagueId)
    .eq('championship', true)
    .order('season_year', { ascending: false });

  return data || [];
}

export async function getSeasonAwards(leagueId: string, seasonYear?: number): Promise<any[]> {
  if (!isSupabaseConfigured() || !supabase) return [];

  let query = supabase
    .from('season_awards')
    .select('*')
    .eq('league_id', leagueId);

  if (seasonYear) {
    query = query.eq('season_year', seasonYear);
  }

  const { data } = await query.order('season_year', { ascending: false });

  return data || [];
}
